<template>
  <PaginaAdministrar
    titulo="Movimentações de Estoque"
    subtitulo="Kardex — Consulta de movimentações"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="false"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <!-- Período de Movimento -->
        <div class="col-md-4">
          <CampoDataIntervalo
            id="filtroPeriodo"
            label="Período do Movimento"
            v-model:modelValueInicio="filtros.tsMovimentoInicio"
            v-model:modelValueFim="filtros.tsMovimentoFim"
          />
        </div>
        <!-- Data Competência -->
        <div class="col-md-4">
          <CampoDataIntervalo
            id="filtroCompetencia"
            label="Data Competência"
            v-model:modelValueInicio="filtros.dataCompetenciaInicio"
            v-model:modelValueFim="filtros.dataCompetenciaFim"
          />
        </div>
        <!-- Filial -->
        <div class="col-md-4">
          <CampoBusca
            id="filtroFilial"
            label="Filial"
            v-model="filtros.idFilial"
            :configuracao-busca="configBuscaFilial"
            @selecionado="val => filtros.nomeFilial = val.identificacao"
          />
        </div>
        <!-- Local Estoque -->
        <div class="col-md-4">
          <CampoBusca
            id="filtroLocalEstoque"
            label="Local Estoque"
            v-model="filtros.idLocalEstoque"
            :configuracao-busca="configBuscaLocalEstoque"
            @selecionado="val => filtros.nomeLocalEstoque = val.descricao"
          />
        </div>
        <!-- Produto -->
        <div class="col-md-4">
          <CampoBusca
            id="filtroProduto"
            label="Produto"
            v-model="filtros.idProduto"
            :configuracao-busca="configBuscaProduto"
            @selecionado="val => { filtros.idProduto = val.idProduto; filtros.nomeProduto = val.nome }"
          />
        </div>
        <!-- Sentido -->
        <div class="col-md-3">
          <label class="form-label">Sentido</label>
          <select class="form-select" v-model="filtros.lstSentido" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option value="TP_MOVIMENTO1">Entrada</option>
            <option value="TP_MOVIMENTO2">Saída</option>
          </select>
        </div>
        <!-- Tipo Operação -->
        <div class="col-md-3">
          <label class="form-label">Tipo Operação</label>
          <select class="form-select" v-model="filtros.lstTipoOperacao" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option value="IMPORTACAO_RECEBIMENTO">Importação</option>
            <option value="COMPRA_NACIONAL">Compra Nacional</option>
            <option value="TRANSFERENCIA">Transferência</option>
            <option value="AJUSTE">Ajuste</option>
            <option value="VENDA">Venda</option>
          </select>
        </div>
        <!-- Status Movimento -->
        <div class="col-md-3">
          <label class="form-label">Status Mov.</label>
          <select class="form-select" v-model="filtros.lstStatusMovimento" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option value="ATIVO">Ativo</option>
            <option value="ESTORNADO">Estornado</option>
          </select>
          <div class="form-text mt-1 mb-3 invisible">&nbsp;</div>
        </div>
        <!-- Documento -->
        <div class="col-md-3">
          <label class="form-label">Nº Documento</label>
          <input
            type="text"
            class="form-control"
            v-model="filtros.numeroDocumentoLegal"
            placeholder="Buscar..."
            @keydown.enter.prevent="aplicarFiltros"
          />
        </div>
        <!-- Embarque -->
        <div class="col-md-3">
          <label class="form-label">Embarque #</label>
          <input
            type="number"
            class="form-control"
            v-model.number="filtros.idEmbarque"
            placeholder="ID"
            @keydown.enter.prevent="aplicarFiltros"
          />
          <div class="form-text mt-1 mb-3 invisible">&nbsp;</div>
        </div>
        <!-- Botões -->
        <div class="col-md-12 text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma movimentação encontrada."
      row-key="idMovimentacaoEstoque"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      modulo-layout="MovimentacoesEstoque"
      :colunas-padrao="colunasPadrao"
      :exportando-progresso="exportandoProgresso"
      :dados-exportar="dadosExportar"
      titulo-exportacao="Movimentações de Estoque"
      :filtros-exportacao="descricaoFiltros"
      @sort-change="handleSortChange"
      @show-notification="emitNotification"
      @exportar-dados="handleExportarDados"
    >
      <!-- ID -->
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idMovimentacaoEstoque }}</span>
      </template>
      <!-- Data/Hora -->
      <template #tsMovimento="{ row }">
        {{ formatarDataHora(row.tsMovimento) }}
      </template>
      <!-- Data Competência -->
      <template #dataCompetencia="{ row }">
        {{ formatarData(row.dataCompetencia) }}
      </template>
      <!-- Status Movimento -->
      <template #lstStatusMovimento="{ row }">
        <span class="badge badge-ghost-secondary">{{ getTextoLista('LstStatusMovimento', row.lstStatusMovimento) }}</span>
      </template>
      <!-- Sentido -->
      <template #lstSentido="{ row }">
        <span class="badge" :class="badgeClasseSentido(row.lstSentido)">
          {{ getTextoLista('LstSentido', row.lstSentido) }}
        </span>
      </template>
      <!-- Tipo Operação -->
      <template #lstTipoOperacao="{ row }">
        <span class="badge badge-ghost-secondary">{{ getTextoLista('LstTipoOperacao', row.lstTipoOperacao) }}</span>
      </template>
      <!-- Produto -->
      <template #nomeProduto="{ row }">
        <span class="fw-medium text-body-emphasis">{{ row.nomeProduto || '-' }}</span>
      </template>
      <!-- Filial -->
      <template #nomeFilial="{ row }">
        {{ row.nomeFilial || '-' }}
      </template>
      <!-- Local Estoque -->
      <template #nomeLocalEstoque="{ row }">
        {{ row.nomeLocalEstoque || '-' }}
      </template>
      <!-- Embarque -->
      <template #idEmbarque="{ row }">
        <span v-if="row.idEmbarque" class="text-muted">#{{ row.idEmbarque }}</span>
        <span v-else class="text-muted">-</span>
      </template>
      <!-- Status Lote -->
      <template #lstStatusLoteFabricacao="{ row }">
        <span class="badge badge-ghost-secondary">{{ getTextoLista('LstStatusLoteFabricacao', row.lstStatusLoteFabricacao) }}</span>
      </template>
      <!-- Usuário Criação -->
      <template #nomeUsuarioCriacao="{ row }">
        <span class="text-muted small">{{ row.nomeUsuarioCriacao || '-' }}</span>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<script setup>
import { ref, reactive, computed, nextTick, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePaginacao } from '@/composables/usePaginacao'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { filialSearch } from '@/search-definitions/filialSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'

const emit = defineEmits(['show-notification'])
const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const emitNotification = (p) => emit('show-notification', p)

// ============================================================
// Estado
// ============================================================
const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)
const listas = reactive({})
const isLoadingListas = ref(false)

// Controle de concorrência: cancela requisição anterior se uma nova for disparada
let abortController = null

// Estado da exportação
const exportandoProgresso = ref({ total: 0, carregados: 0 })
const dadosExportar = ref(null)

const sortField = ref('')
const sortOrder = ref('desc')

const filtros = reactive({
  tsMovimentoInicio: '',
  tsMovimentoFim: '',
  dataCompetenciaInicio: '',
  dataCompetenciaFim: '',
  idFilial: '',
  nomeFilial: '',
  idLocalEstoque: '',
  nomeLocalEstoque: '',
  idProduto: '',
  nomeProduto: '',
  lstSentido: '',
  lstTipoOperacao: '',
  lstStatusMovimento: '',
  numeroDocumentoLegal: '',
  idEmbarque: null
})

// Descrição textual dos filtros ativos para incluir na exportação
const descricaoFiltros = computed(() => {
  const partes = []
  if (filtros.tsMovimentoInicio || filtros.tsMovimentoFim) {
    partes.push(`Período: ${filtros.tsMovimentoInicio || '...'} ~ ${filtros.tsMovimentoFim || '...'}`)
  }
  if (filtros.dataCompetenciaInicio || filtros.dataCompetenciaFim) {
    partes.push(`Competência: ${filtros.dataCompetenciaInicio || '...'} ~ ${filtros.dataCompetenciaFim || '...'}`)
  }
  if (filtros.nomeFilial) partes.push(`Filial: ${filtros.nomeFilial}`)
  if (filtros.nomeLocalEstoque) partes.push(`Local: ${filtros.nomeLocalEstoque}`)
  if (filtros.nomeProduto) partes.push(`Produto: ${filtros.nomeProduto}`)
  if (filtros.lstSentido) partes.push(`Sentido: ${getTextoLista('LstSentido', filtros.lstSentido)}`)
  if (filtros.lstTipoOperacao) partes.push(`Tipo Operação: ${filtros.lstTipoOperacao}`)
  if (filtros.lstStatusMovimento) partes.push(`Status: ${filtros.lstStatusMovimento}`)
  if (filtros.numeroDocumentoLegal) partes.push(`Nº Doc.: ${filtros.numeroDocumentoLegal}`)
  if (filtros.idEmbarque) partes.push(`Embarque: #${filtros.idEmbarque}`)
  return partes.length ? partes.join(' | ') : 'Nenhum filtro aplicado'
})

// ============================================================
// Colunas Disponíveis (para o sistema de layouts)
// ============================================================
const colunasPadrao = [
  // ================================================================
  // Grupo 1 — Identificação
  // ================================================================
  { field: 'idMovimentacaoEstoque', label: 'ID', width: 80, sortable: true, align: 'left' },
  { field: 'tsMovimento', label: 'Data/Hora Movimento', width: 160, sortable: true, align: 'center' },
  { field: 'dataCompetencia', label: 'Data Competência', width: 120, sortable: true, align: 'center' },
  {
    field: 'lstStatusMovimento',
    label: 'Status do Movimento',
    width: 130,
    sortable: true,
    align: 'center',
    formatter: (val) => getTextoLista('LstStatusMovimento', val)
  },

  // ================================================================
  // Grupo 2 — Produto e Localização
  // ================================================================
  { field: 'skuProduto', label: 'SKU', width: 120, align: 'left' },
  { field: 'nomeProduto', label: 'Nome do Produto', width: 220, align: 'left' },
  { field: 'nomeFilial', label: 'Filial', width: 150, align: 'left' },
  { field: 'nomeLocalEstoque', label: 'Local de Estoque', width: 170, align: 'left' },
  { field: 'idFilial', label: 'ID Filial', width: 80, align: 'left', visible: false },
  { field: 'idLocalEstoque', label: 'ID Local Estoque', width: 100, align: 'left', visible: false },

  // ================================================================
  // Grupo 3 — Direção do Movimento
  // ================================================================
  {
    field: 'lstSentido',
    label: 'Sentido',
    width: 100,
    sortable: true,
    align: 'center',
    formatter: (val) => getTextoLista('LstSentido', val)
  },
  {
    field: 'lstTipoOperacao',
    label: 'Tipo de Operação',
    width: 150,
    sortable: true,
    align: 'center',
    formatter: (val) => getTextoLista('LstTipoOperacao', val)
  },

  // ================================================================
  // Grupo 4 — Quantidades
  // ================================================================
  {
    field: 'quantidadeUnBasica',
    label: 'Qtd Movimentada',
    width: 120,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 4)
  },
  {
    field: 'lstUnidadeMedidaBasica',
    label: 'Unidade Medida Básica',
    width: 120,
    align: 'left',
    formatter: (val) => getTextoLista('LstUnidadeMedidaBasica', val)
  },
  {
    field: 'quantidadeVolumes',
    label: 'Qtd Volumes',
    width: 100,
    align: 'right',
    formatter: (val) => val != null ? formatarNumero(val, 0) : '-',
    visible: false
  },
  {
    field: 'lstTipoVolume',
    label: 'Tipo de Volume',
    width: 110,
    align: 'left',
    formatter: (val) => getTextoLista('LstTipoVolume', val),
    visible: false
  },

  // ================================================================
  // Grupo 5 — Saldos Kardex
  // ================================================================
  { field: 'saldoQtdUnBasicaPreMov', label: 'Saldo Anterior (Qtd)', width: 140, sortable: true, align: 'right' },
  { field: 'saldoQtdUnBasicaPosMov', label: 'Saldo Posterior (Qtd)', width: 140, sortable: true, align: 'right' },

  // ================================================================
  // Grupo 6 — Custos Padrão (BRL)
  // ================================================================
  {
    field: 'custoUnitMovPadrao',
    label: 'Custo Unitário (BRL)',
    width: 140,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 6)
  },
  {
    field: 'custoTotalMovPadrao',
    label: 'Custo Total (BRL)',
    width: 140,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 2)
  },
  {
    field: 'custoMedioUnitPreMovPadrao',
    label: 'Custo Médio Anterior (BRL)',
    width: 170,
    sortable: true,
    align: 'right',
    visible: false,
    formatter: (val) => formatarNumero(val, 6)
  },
  {
    field: 'custoMedioUnitPosMovPadrao',
    label: 'Custo Médio Posterior (BRL)',
    width: 170,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 6)
  },
  {
    field: 'valorTotalEstoquePreMovPadrao',
    label: 'Valor Estoque Anterior (BRL)',
    width: 180,
    sortable: true,
    align: 'right',
    visible: false,
    formatter: (val) => formatarNumero(val, 2)
  },
  {
    field: 'valorTotalEstoquePosMovPadrao',
    label: 'Valor Estoque Posterior (BRL)',
    width: 180,
    sortable: true,
    align: 'right',
    formatter: (val) => formatarNumero(val, 2)
  },

  // ================================================================
  // Grupo 7 — Custos Gerencial (USD)
  // ================================================================
  {
    field: 'custoUnitMovGerencial',
    label: 'Custo Unitário (USD)',
    width: 150,
    sortable: true,
    align: 'right',
    visible: false,
    formatter: (val) => formatarNumero(val, 6)
  },
  {
    field: 'custoTotalMovGerencial',
    label: 'Custo Total (USD)',
    width: 150,
    sortable: true,
    align: 'right',
    visible: false,
    formatter: (val) => formatarNumero(val, 2)
  },
  {
    field: 'custoMedioUnitPreMovGerencial',
    label: 'Custo Médio Anterior (USD)',
    width: 180,
    sortable: true,
    align: 'right',
    visible: false,
    formatter: (val) => formatarNumero(val, 6)
  },
  {
    field: 'custoMedioUnitPosMovGerencial',
    label: 'Custo Médio Posterior (USD)',
    width: 180,
    sortable: true,
    align: 'right',
    visible: false,
    formatter: (val) => formatarNumero(val, 6)
  },
  {
    field: 'valorTotalEstoquePreMovGerencial',
    label: 'Valor Estoque Anterior (USD)',
    width: 190,
    sortable: true,
    align: 'right',
    visible: false,
    formatter: (val) => formatarNumero(val, 2)
  },
  {
    field: 'valorTotalEstoquePosMovGerencial',
    label: 'Valor Estoque Posterior (USD)',
    width: 190,
    sortable: true,
    align: 'right',
    visible: false,
    formatter: (val) => formatarNumero(val, 2)
  },

  // ================================================================
  // Grupo 8 — Rastreabilidade
  // ================================================================
  { field: 'numeroDocumentoLegal', label: 'Nº Documento Legal', width: 150, align: 'left' },
  { field: 'idDocumentoEntradaItem', label: 'ID Doc. Entrada Item', width: 130, align: 'left', visible: false },
  { field: 'idEmbarque', label: 'ID Embarque', width: 100, align: 'center', visible: false },
  { field: 'idLoteFabricacao', label: 'ID Lote Fabricação', width: 110, align: 'left', visible: false },
  { field: 'numeroLoteFornecedor', label: 'Nº Lote Fornecedor', width: 140, align: 'left' },
  {
    field: 'dataFabricacao',
    label: 'Data Fabricação do Lote',
    width: 140,
    align: 'center',
    formatter: (val) => val ? formatarData(val) : '-',
    visible: false
  },
  {
    field: 'dataValidade',
    label: 'Data Validade do Lote',
    width: 140,
    align: 'center',
    formatter: (val) => val ? formatarData(val) : '-',
    visible: false
  },
  {
    field: 'lstStatusLoteFabricacao',
    label: 'Status do Lote',
    width: 120,
    align: 'center',
    formatter: (val) => getTextoLista('LstStatusLoteFabricacao', val),
    visible: false
  },

  // ================================================================
  // Grupo 9 — Auditoria
  // ================================================================
  {
    field: 'tsCriacao',
    label: 'Data Criação do Registro',
    width: 170,
    align: 'center',
    formatter: (val) => val ? formatarDataHora(val) : '-',
    visible: false
  },
  { field: 'nomeUsuarioCriacao', label: 'Criado por', width: 160, align: 'left', visible: false }
]

// Mapeamento campo → nome do slot no template
// O nome do campo no DTO pode diferir do nome do slot (ex: idMovimentacaoEstoque → slot "id")
const campoSlotMap = {
  idMovimentacaoEstoque: 'id',
  tsMovimento: 'tsMovimento',
  dataCompetencia: 'dataCompetencia',
  lstStatusMovimento: 'lstStatusMovimento',
  lstSentido: 'lstSentido',
  lstTipoOperacao: 'lstTipoOperacao',
  nomeProduto: 'nomeProduto',
  nomeFilial: 'nomeFilial',
  nomeLocalEstoque: 'nomeLocalEstoque',
  idEmbarque: 'idEmbarque',
  lstStatusLoteFabricacao: 'lstStatusLoteFabricacao',
  nomeUsuarioCriacao: 'nomeUsuarioCriacao'
}

// Colunas ativas (padrão visível ao usuário)
const colunas = computed(() => {
  // Retorna as colunas padrão — o AdminTable aplica o layout salvo por cima
  return colunasPadrao.map(c => ({
    ...c,
    slotName: campoSlotMap[c.field] || undefined
  }))
})

// ============================================================
// Configurações de busca
// ============================================================
const configBuscaProduto = computed(() => ({
  ...criarConfigBuscaProduto(),
  titulo: 'Buscar Produto',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva }
}))

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva }
}))

const configBuscaLocalEstoque = computed(() => {
  // Tipos de local: [] = mostra todos os tipos. O filtro de Filial é único nesta tela,
  // então a correlação por idFilial está correta (diferente das telas com múltiplas filiais).
  const base = criarConfigBuscaLocalEstoque(authStore.organizacaoAtiva, filtros.idFilial, [])
  return { ...base, titulo: 'Buscar Local de Estoque' }
})

// ============================================================
// Busca de dados
// ============================================================
async function buscarDados (pageNumber = 1) {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  // Cancela requisição anterior ainda em andamento para evitar race condition
  if (abortController) {
    abortController.abort()
  }
  abortController = new AbortController()

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.idFilial) params.append('idFilial', filtros.idFilial)
    if (filtros.idLocalEstoque) params.append('idLocalEstoque', filtros.idLocalEstoque)
    if (filtros.idProduto) params.append('idProduto', filtros.idProduto)
    if (filtros.lstSentido) params.append('lstSentido', filtros.lstSentido)
    if (filtros.lstTipoOperacao) params.append('lstTipoOperacao', filtros.lstTipoOperacao)
    if (filtros.lstStatusMovimento) params.append('lstStatusMovimento', filtros.lstStatusMovimento)
    if (filtros.numeroDocumentoLegal) params.append('numeroDocumentoLegal', filtros.numeroDocumentoLegal)
    if (filtros.idEmbarque) params.append('idEmbarque', filtros.idEmbarque)
    if (filtros.tsMovimentoInicio) params.append('tsMovimentoInicio', filtros.tsMovimentoInicio)
    if (filtros.tsMovimentoFim) params.append('tsMovimentoFim', filtros.tsMovimentoFim)
    if (filtros.dataCompetenciaInicio) params.append('dataCompetenciaInicio', filtros.dataCompetenciaInicio)
    if (filtros.dataCompetenciaFim) params.append('dataCompetenciaFim', filtros.dataCompetenciaFim)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/MovimentacoesEstoque?${params.toString()}`, {
      signal: abortController.signal
    })
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    // Ignora erros de requisição cancelada (race condition controlada)
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

// ============================================================
// Exportação de dados (AdminTable)
// ============================================================
async function handleExportarDados () {
  if (!authStore.empresaAtiva) {
    emitNotification({ message: 'Selecione uma empresa antes de exportar.', type: 'warning' })
    return
  }

  // O backend retorna camelCase (totalCount), mas o usePaginacao inicializa com TotalCount (PascalCase)
  const totalRegistros = paginacao.value.totalCount ?? paginacao.value.TotalCount ?? 0
  if (!totalRegistros) {
    emitNotification({ message: 'Nenhum dado para exportar.', type: 'warning' })
    return
  }

  exportandoProgresso.value = { total: totalRegistros, carregados: 0 }
  dadosExportar.value = null

  const pageSize = paginacao.value.PageSize || 25
  const totalPages = Math.ceil(totalRegistros / pageSize)
  let todosDados = []

  try {
    for (let page = 1; page <= totalPages; page++) {
      const params = new URLSearchParams()
      params.append('pageNumber', page)
      params.append('pageSize', pageSize)

      // Injeta filtros ativos (mesma lógica do buscarDados)
      if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
      if (filtros.idFilial) params.append('idFilial', filtros.idFilial)
      if (filtros.idLocalEstoque) params.append('idLocalEstoque', filtros.idLocalEstoque)
      if (filtros.idProduto) params.append('idProduto', filtros.idProduto)
      if (filtros.lstSentido) params.append('lstSentido', filtros.lstSentido)
      if (filtros.lstTipoOperacao) params.append('lstTipoOperacao', filtros.lstTipoOperacao)
      if (filtros.lstStatusMovimento) params.append('lstStatusMovimento', filtros.lstStatusMovimento)
      if (filtros.numeroDocumentoLegal) params.append('numeroDocumentoLegal', filtros.numeroDocumentoLegal)
      if (filtros.idEmbarque) params.append('idEmbarque', filtros.idEmbarque)
      if (filtros.tsMovimentoInicio) params.append('tsMovimentoInicio', filtros.tsMovimentoInicio)
      if (filtros.tsMovimentoFim) params.append('tsMovimentoFim', filtros.tsMovimentoFim)
      if (filtros.dataCompetenciaInicio) params.append('dataCompetenciaInicio', filtros.dataCompetenciaInicio)
      if (filtros.dataCompetenciaFim) params.append('dataCompetenciaFim', filtros.dataCompetenciaFim)
      if (sortField.value) params.append('sortField', sortField.value)
      if (sortOrder.value) params.append('sortOrder', sortOrder.value)

      const response = await api.get(`/MovimentacoesEstoque?${params.toString()}`)
      const dados = response.data
      if (Array.isArray(dados)) {
        todosDados = todosDados.concat(dados)
      }

      exportandoProgresso.value = { total: totalRegistros, carregados: todosDados.length }
    }

    dadosExportar.value = [...todosDados]

    // Aguarda o watch do AdminTable processar os dados, depois reseta o progresso
    await nextTick()
    exportandoProgresso.value = { total: 0, carregados: 0 }
  } catch (e) {
    console.error('[EstoqueMovimentacoes] Erro na exportação:', e)
    emitNotification({ message: 'Erro ao coletar dados para exportação.', type: 'error' })
    exportandoProgresso.value = { total: 0, carregados: 0 }
  }
}

// ============================================================
// Ações
// ============================================================
const aplicarFiltros = () => buscarDados(1)
const limparFiltros = () => {
  Object.keys(filtros).forEach(k => { filtros[k] = '' })
  filtros.idEmbarque = null
  buscarDados(1)
}
const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}
const mudarTamanhoPagina = (n) => {
  paginacao.value.PageSize = n
  buscarDados(1)
}
const handleSortChange = () => buscarDados(1)

// ============================================================
// Formatação
// ============================================================
function formatarDataHora (data) {
  if (!data) return '-'
  return formatarDataHoraBR ? formatarDataHoraBR(new Date(data)) : new Date(data).toLocaleString('pt-BR')
}
function formatarData (data) {
  if (!data) return '-'
  return formatarDataBR ? formatarDataBR(data) : new Date(data).toLocaleDateString('pt-BR')
}
function formatarNumero (valor, decimais = 2) {
  if (valor === null || valor === undefined) return '-'
  return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: decimais, maximumFractionDigits: decimais })
}

// ============================================================
// Sentido — valores reais da lista estrutural TP_MOVIMENTO
// (Entrada = "TP_MOVIMENTO1", Saída = "TP_MOVIMENTO2" — NUNCA literais "ENTRADA"/"SAIDA")
// ============================================================
const LST_SENTIDO_ENTRADA = 'TP_MOVIMENTO1'
function badgeClasseSentido (lstSentido) {
  return lstSentido === LST_SENTIDO_ENTRADA ? 'badge-ghost-success' : 'badge-ghost-danger'
}

// ============================================================
// Resolução de Listas (Lst*)
// ============================================================
function getTextoLista (listaKey, id) {
  if (!id) return '-'
  const lista = listas[listaKey] || []
  const item = lista.find(i =>
    String(i.idListaSistemaItem) === String(id) ||
    String(i.idListaClienteItem) === String(id)
  )
  return item ? item.textoLista : id
}

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const [resultMov, resultLote] = await Promise.all([
      metadataStore.carregarListasParaTabelas(['MovimentacaoEstoque']),
      metadataStore.carregarListasParaTabelas(['LoteFabricacao'])
    ])
    Object.assign(listas, resultMov, resultLote)
  } catch (error) {
    console.error('Erro ao carregar listas auxiliares:', error)
  } finally {
    isLoadingListas.value = false
  }
}

// ============================================================
// Ciclo de vida
// ============================================================
watch(() => authStore.empresaAtiva, async (n) => {
  if (n) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    Object.keys(listas).forEach(k => delete listas[k])
  }
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})
onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})
</script>

<style scoped>
/* Estilos adicionais específicos da tela de movimentações */
</style>
